
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { registerSchema } from "./schema";
import PersonalInfoFields from "./PersonalInfoFields";
import PasswordField from "../common/PasswordField";
import TermsCheckbox from "./TermsCheckbox";
import FormHeader from "../common/FormHeader";
import LoginPrompt from "../common/LoginPrompt";
import { RegistrationSocialLogin } from "./RegistrationSocialLogin";
import { useRegistration } from "@/hooks/use-registration";
import type { RegisterFormData } from "@/hooks/use-registration";

export default function RegisterForm() {
  const navigate = useNavigate();
  const {
    isLoading,
    handleSignUp
  } = useRegistration();

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      agreeToTerms: false
    }
  });

  return (
    <div className="space-y-6">
      <FormHeader 
        title="Create Your Account" 
        subtitle="Join to find the care options you need" 
      />
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSignUp)} className="space-y-4">
          <PersonalInfoFields control={form.control} />
          <PasswordField 
            control={form.control}
            name="password"
            label="Password"
            placeholder="Create a strong password"
            autoComplete="new-password"
            helpText="Password must be at least 8 characters"
            required
          />
          <TermsCheckbox control={form.control} />

          <Button type="submit" disabled={isLoading} className="w-full h-11 mt-6 bg-grandpal-green hover:bg-grandpal-green-dark/90 text-white font-medium">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating account...
              </>
            ) : (
              "Create Account"
            )}
          </Button>
        </form>
      </Form>
      
      <RegistrationSocialLogin />
      
      <LoginPrompt
        text="Already have an account?"
        linkText="Sign in"
        onClick={() => navigate("/auth/login")}
      />
    </div>
  );
}
